import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Table,
  Tag,
  Button,
  Tabs,
  Modal,
  Descriptions,
  Space,
  message,
  DatePicker,
  Spin,
  Input,
  Form,
  Select,
  InputNumber,
  Switch,
  Divider,
} from "antd";

import {
  SafetyOutlined,
  WarningOutlined,
  FileSearchOutlined,
  DownloadOutlined,
  EyeOutlined,
  PlusOutlined,
  SettingOutlined,
  DeleteOutlined,
  EditOutlined,
} from "@ant-design/icons";

import moment from "moment";

import useRequest from "../hooks/useRequest";
import { ShowToast, Severty } from "../helper/toast";

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { RangePicker } = DatePicker;
const { TextArea } = Input;
const { Option } = Select;

function ComplianceManager() {
  const { request } = useRequest();

  const [selectedSTR, setSelectedSTR] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [ruleModalOpen, setRuleModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);

  const [loading, setLoading] = useState(false);
  const [tmrLoading, setTmrLoading] = useState(false);
  const [strLoading, setStrLoading] = useState(false);
  const [ruleLoading, setRuleLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);

  const [strData, setStrData] = useState([]);
  const [strOverallCount, setStrOverallCount] = useState(0);
  const [strPage, setStrPage] = useState(1);
  const [strLimit, setStrLimit] = useState(10);

  const [tmrData, setTmrData] = useState([]);
  const [tmrOverallCount, setTmrOverallCount] = useState(0);
  const [tmrPage, setTmrPage] = useState(1);
  const [tmrLimit, setTmrLimit] = useState(10);
  const [tmrDateRange, setTmrDateRange] = useState([]);

  const [rulesData, setRulesData] = useState([]);
  const [ruleOverallCount, setRuleOverallCount] = useState(0);
  const [rulePage, setRulePage] = useState(1);
  const [ruleLimit, setRuleLimit] = useState(10);

  const [investigatorNotes, setInvestigatorNotes] = useState("");

  const [form] = Form.useForm();

  // --------------------------------------------------
  // CONDITION FIELDS
  // --------------------------------------------------

  const conditionFields = [
    {
      label: "Transaction Amount",
      value: "transaction_amount",
    },
    {
      label: "Daily Transaction Count",
      value: "daily_transaction_count",
    },
  ];

  const operators = [
    {
      label: "Equals",
      value: "eq",
    },
    {
      label: "Not Equals",
      value: "neq",
    },
    {
      label: "Greater Than",
      value: "gt",
    },
    {
      label: "Less Than",
      value: "lt",
    },
    {
      label: "Greater or Equal",
      value: "gte",
    },
    {
      label: "Less or Equal",
      value: "lte",
    },
  ];

  // --------------------------------------------------
  // FETCH DATA
  // --------------------------------------------------

  const fetchTmrData = useCallback(() => {
    setTmrLoading(true);
    let url = `/api/admin/compliance/tmr-alerts?page=${tmrPage}&limit=${tmrLimit}`;
    if (tmrDateRange && tmrDateRange.length === 2) {
      url += `&start_date=${tmrDateRange[0].format("YYYY-MM-DD")}&end_date=${tmrDateRange[1].format("YYYY-MM-DD")}`;
    }

    request({
      url,
      method: "GET",
      onSuccess: (res) => {
        setTmrData(res.data || []);
        setTmrOverallCount(res.overall_count || res.pagination?.total || 0);
        setTmrLoading(false);
      },
      onError: (err) => {
        setTmrLoading(false);
        ShowToast(
          err.message || "Failed to fetch TMR alerts",
          Severty.ERROR
        );
      },
    });
  }, [request, tmrPage, tmrLimit, tmrDateRange]);

  const fetchStrData = useCallback(() => {
    setStrLoading(true);
    let url = `/api/admin/compliance/str-reports?page=${strPage}&limit=${strLimit}`;
    request({
      url,
      method: "GET",
      onSuccess: (res) => {
        setStrData(res.data || []);
        setStrOverallCount(res.overall_count || res.pagination?.total || 0);
        setStrLoading(false);
      },
      onError: (err) => {
        setStrLoading(false);
        ShowToast(
          err.message || "Failed to fetch STR reports",
          Severty.ERROR
        );
      },
    });
  }, [request, strPage, strLimit]);

  const fetchRuleData = useCallback(() => {
    setRuleLoading(true);
    let url = `/api/admin/compliance/tmr-rules?page=${rulePage}&limit=${ruleLimit}`;
    request({
      url,
      method: "GET",
      onSuccess: (res) => {
        setRulesData(res.data || []);
        setRuleOverallCount(res.overall_count || res.pagination?.total || 0);
        setRuleLoading(false);
      },
      onError: (err) => {
        setRuleLoading(false);
        ShowToast(
          err.message || "Failed to fetch rules",
          Severty.ERROR
        );
      },
    });
  }, [request, rulePage, ruleLimit]);

  useEffect(() => {
    fetchStrData();
  }, [strPage, strLimit]);

  useEffect(() => {
    fetchRuleData();
  }, [rulePage, ruleLimit]);

  useEffect(() => {
    fetchTmrData();
  }, [tmrPage, tmrLimit, tmrDateRange]);

  // --------------------------------------------------
  // ACTIONS
  // --------------------------------------------------

  const handleExport = (type) => {
    message.success(`${type} exported successfully`);
  };

  const handleUpdateSTRStatus = (reportId, status, notes) => {
    setUpdateLoading(true);

    request({
      url: `/api/admin/compliance/str-reports/${reportId}`,
      method: "PUT",
      data: {
        status,
        notes,
      },
      onSuccess: () => {
        setUpdateLoading(false);
        setReviewOpen(false);

        ShowToast(
          `Case updated to ${status}`,
          Severty.SUCCESS
        );

        fetchStrData();
      },
      onError: (err) => {
        setUpdateLoading(false);

        ShowToast(
          err.message || "Update failed",
          Severty.ERROR
        );
      },
    });
  };

  const handleEscalateTMR = (alertId) => {
    let notes = "";

    Modal.confirm({
      title: "Escalate to STR",
      content: (
        <div style={{ marginTop: 10 }}>
          <Text>
            Provide investigator notes for this escalation:
          </Text>

          <TextArea
            rows={4}
            style={{ marginTop: 10 }}
            onChange={(e) => {
              notes = e.target.value;
            }}
          />
        </div>
      ),

      onOk: () => {
        return new Promise((resolve, reject) => {
          request({
            url: "/api/admin/compliance/tmr-alerts",
            method: "POST",
            data: {
              alert_id: alertId,
              notes,
            },

            onSuccess: () => {
              ShowToast(
                "Escalated successfully",
                Severty.SUCCESS
              );

              fetchStrData();
              fetchTmrData();
              resolve();
            },

            onError: (err) => {
              ShowToast(
                err.message || "Escalation failed",
                Severty.ERROR
              );

              reject();
            },
          });
        });
      },
    });
  };

  const handleSaveRule = (values) => {
    setUpdateLoading(true);
    const isEdit = !!editingRule;
    const url = isEdit
      ? `/api/admin/compliance/tmr-rules/${editingRule.id}`
      : "/api/admin/compliance/tmr-rules";
    const method = isEdit ? "PUT" : "POST";

    request({
      url,
      method,
      data: values,
      onSuccess: () => {
        setUpdateLoading(false);
        setRuleModalOpen(false);
        setEditingRule(null);
        form.resetFields();
        ShowToast(
          `Monitoring rule ${isEdit ? "updated" : "created"} successfully`,
          Severty.SUCCESS
        );
        fetchRuleData();
      },
      onError: (err) => {
        setUpdateLoading(false);
        ShowToast(
          err.message || `Failed to ${isEdit ? "update" : "create"} rule`,
          Severty.ERROR
        );
      },
    });
  };

  const handleOpenEditRuleModal = (rule) => {
    setEditingRule(rule);
    form.setFieldsValue({
      name: rule.name,
      description: rule.description,
      risk_level: rule.risk_level,
      is_active: rule.is_active,
      conditions: rule.conditions && rule.conditions.length > 0 ? rule.conditions : [{}],
    });
    setRuleModalOpen(true);
  };

  const handleDeleteRule = (ruleId) => {
    Modal.confirm({
      title: "Delete Monitoring Rule",
      content: "Are you sure you want to delete this monitoring rule? This action cannot be undone.",
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "No",
      onOk: () => {
        return new Promise((resolve, reject) => {
          request({
            url: `/api/admin/compliance/tmr-rules/${ruleId}`,
            method: "DELETE",
            onSuccess: () => {
              ShowToast("Rule deleted successfully", Severty.SUCCESS);
              fetchRuleData();
              resolve();
            },
            onError: (err) => {
              ShowToast(err.message || "Failed to delete rule", Severty.ERROR);
              reject();
            },
          });
        });
      },
    });
  };

  const handleToggleRuleActive = (ruleId, is_active) => {
    request({
      url: `/api/admin/compliance/tmr-rules/${ruleId}`,
      method: "PUT",
      data: { is_active },
      onSuccess: () => {
        ShowToast("Rule status updated successfully", Severty.SUCCESS);
        setRulesData((prevRules) =>
          prevRules.map((rule) =>
            rule.id === ruleId ? { ...rule, is_active } : rule
          )
        );
      },
      onError: (err) => {
        ShowToast(err.message || "Failed to update rule status", Severty.ERROR);
      },
    });
  };

  // --------------------------------------------------
  // STR TABLE
  // --------------------------------------------------

  const strColumns = [
    {
      title: "Report ID",
      dataIndex: "report_id",
    },

    {
      title: "User",
      render: (_, record) => (
        <>
          <div>{record.user_name}</div>

          <Text type="secondary">
            ID: {record.user_id_str}
          </Text>
        </>
      ),
    },

    {
      title: "Transaction Ref",
      dataIndex: "transaction_ref",
      render: (val) => val || "N/A",
    },

    {
      title: "Amount",
      render: (_, record) =>
        record.amount ? `$${record.amount}` : "N/A",
    },

    {
      title: "Risk",
      render: (_, record) => (
        <Tag
          color={
            record.risk_level === "HIGH"
              ? "red"
              : record.risk_level === "MEDIUM"
                ? "gold"
                : "green"
          }
        >
          {record.risk_level}
        </Tag>
      ),
    },

    {
      title: "Status",
      render: (_, record) => (
        <Tag color="blue">{record.status}</Tag>
      ),
    },

    {
      title: "Date",
      render: (_, record) =>
        moment(record.created_at).format(
          "DD MMM YYYY HH:mm"
        ),
    },

    {
      title: "Action",
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => {
            setSelectedSTR(record);
            setInvestigatorNotes(
              record.investigator_notes || ""
            );
            setReviewOpen(true);
          }}
        >
          Review
        </Button>
      ),
    },
  ];

  // --------------------------------------------------
  // TMR TABLE
  // --------------------------------------------------

  const tmrColumns = [
    {
      title: "Alert ID",
      dataIndex: "alert_id",
    },

    {
      title: "Rule Triggered",
      dataIndex: "rule_name",
      render: (val) => (
        <Tag color="volcano">{val}</Tag>
      ),
    },

    {
      title: "Risk Level",
      dataIndex: "risk_level",
      render: (val) => (
        <Tag
          color={
            val === "HIGH"
              ? "red"
              : val === "MEDIUM"
                ? "gold"
                : "green"
          }
        >
          {val || "LOW"}
        </Tag>
      ),
    },

    {
      title: "User",
      dataIndex: "user_name",
    },

    {
      title: "Amount",
      render: (_, record) =>
        record.amount ? `$${record.amount}` : "N/A",
    },

    {
      title: "Type",
      dataIndex: "type",
    },

    {
      title: "Status",
      render: (_, record) => (
        <Tag
          color={
            record.status === "SUCCESS"
              ? "green"
              : "orange"
          }
        >
          {record.status}
        </Tag>
      ),
    },

    {
      title: "Date",
      render: (_, record) =>
        moment(record.created_at).format(
          "DD MMM YYYY HH:mm"
        ),
    },

    {
      title: "Action",
      render: (_, record) => (
        <Button
          danger
          size="small"
          icon={<WarningOutlined />}
          onClick={() =>
            handleEscalateTMR(record.alert_id)
          }
        >
          Escalate
        </Button>
      ),
    },
  ];

  // --------------------------------------------------
  // RULE TABLE
  // --------------------------------------------------

  const ruleColumns = [
    {
      title: "Rule Name",
      dataIndex: "name",
    },

    {
      title: "Risk Level",
      dataIndex: "risk_level",

      render: (val) => (
        <Tag
          color={
            val === "HIGH"
              ? "red"
              : val === "MEDIUM"
                ? "gold"
                : "green"
          }
        >
          {val}
        </Tag>
      ),
    },

    {
      title: "Conditions",

      render: (_, record) => {
        if (!record.conditions?.length) return "-";

        return record.conditions.map((c, index) => (
          <div key={index}>
            {c.field} {c.operator} {c.value}
          </div>
        ));
      },
    },

    {
      title: "Active",

      dataIndex: "is_active",

      render: (val, record) => (
        <Switch
          checked={val}
          onChange={(checked) => handleToggleRuleActive(record.id, checked)}
        />
      ),
    },

    {
      title: "Action",
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            ghost
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleOpenEditRuleModal(record)}
          >
            Edit
          </Button>
          <Button
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteRule(record.id)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  // --------------------------------------------------
  // UI
  // --------------------------------------------------

  return (
    <div
      style={{
        background: "#f5f7fb",
        minHeight: "100vh",
        padding: 20,
      }}
    >
      {/* HEADER */}

      <Card
        style={{
          borderRadius: 20,
          marginBottom: 25,
          boxShadow: "0 10px 40px rgba(0,0,0,0.05)",
        }}
      >
        <Title level={3}>
          <SafetyOutlined /> Compliance & System Manager
        </Title>

        <Text type="secondary">
          Monitor suspicious activities and manage
          compliance rules
        </Text>
      </Card>

      {/* SUMMARY */}

      <Row gutter={[20, 20]} style={{ marginBottom: 25 }}>
        <Col xs={24} md={6}>
          <Card hoverable>
            <Title level={4}>{strOverallCount}</Title>
            <Text>STR Reports</Text>
          </Card>
        </Col>

        <Col xs={24} md={6}>
          <Card hoverable>
            <Title level={4}>{tmrOverallCount}</Title>
            <Text>Monitoring Alerts</Text>
          </Card>
        </Col>

        <Col xs={24} md={6}>
          <Card hoverable>
            <Title level={4}>
              {
                strData.filter(
                  (i) => i.risk_level === "HIGH"
                ).length
              }
            </Title>

            <Text>High Risk Cases</Text>
          </Card>
        </Col>

        <Col xs={24} md={6}>
          <Card hoverable>
            <Title level={4}>{ruleOverallCount}</Title>
            <Text>Active Rules</Text>
          </Card>
        </Col>
      </Row>

      {/* MAIN */}

      <Card style={{ borderRadius: 20 }}>
        <Tabs defaultActiveKey="1">
          {/* STR */}

          <TabPane
            key="1"
            tab={
              <span>
                <WarningOutlined /> STR Reports
              </span>
            }
          >
            <Space style={{ marginBottom: 15 }}>
              <Button
                icon={<DownloadOutlined />}
                onClick={() => handleExport("CSV")}
              >
                Export CSV
              </Button>
            </Space>

            <Spin spinning={strLoading}>
              <div className="table-responsive">
                <Table
                  columns={strColumns}
                  dataSource={strData}
                  rowKey="report_id"
                  pagination={{ 
                    current: strPage,
                    pageSize: strLimit,
                    total: strOverallCount,
                    onChange: (page, pageSize) => {
                      setStrPage(page);
                      setStrLimit(pageSize);
                    }
                  }}
                />
              </div>
            </Spin>
          </TabPane>

          {/* TMR */}

          <TabPane
            key="2"
            tab={
              <span>
                <FileSearchOutlined /> TMR Monitoring
              </span>
            }
          >
            <Space style={{ marginBottom: 15 }}>
              <RangePicker 
                onChange={(dates) => {
                  setTmrDateRange(dates);
                  setTmrPage(1);
                }}
              />
            </Space>

            <Spin spinning={tmrLoading}>
              <div className="table-responsive">
                <Table
                  columns={tmrColumns}
                  dataSource={tmrData}
                  rowKey="alert_id"
                  pagination={{ 
                    current: tmrPage,
                    pageSize: tmrLimit,
                    total: tmrOverallCount,
                    onChange: (page, pageSize) => {
                      setTmrPage(page);
                      setTmrLimit(pageSize);
                    }
                  }}
                />
              </div>
            </Spin>
          </TabPane>

          {/* RULES */}

          <TabPane
            key="3"
            tab={
              <span>
                <SettingOutlined /> Monitoring Rules
              </span>
            }
          >
            <Space style={{ marginBottom: 15 }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setEditingRule(null);
                  form.resetFields();
                  setRuleModalOpen(true);
                }}
              >
                Create New Rule
              </Button>
            </Space>

            <Spin spinning={ruleLoading}>
              <div className="table-responsive">
                <Table
                  columns={ruleColumns}
                  dataSource={rulesData}
                  rowKey="id"
                  pagination={{ 
                    current: rulePage,
                    pageSize: ruleLimit,
                    total: ruleOverallCount,
                    onChange: (page, pageSize) => {
                      setRulePage(page);
                      setRuleLimit(pageSize);
                    }
                  }}
                />
              </div>
            </Spin>
          </TabPane>
        </Tabs>
      </Card>

      {/* REVIEW MODAL */}

      <Modal
        title="STR Case Investigation"
        open={reviewOpen}
        onCancel={() => setReviewOpen(false)}
        width={700}
        footer={[
          <Button
            key="monitor"
            onClick={() =>
              handleUpdateSTRStatus(
                selectedSTR.report_id,
                "MONITORING",
                investigatorNotes
              )
            }
          >
            Mark as Monitoring
          </Button>,

          <Button
            key="report"
            danger
            onClick={() =>
              handleUpdateSTRStatus(
                selectedSTR.report_id,
                "REPORTED",
                investigatorNotes
              )
            }
          >
            File Regulatory Report
          </Button>,

          <Button
            key="close"
            type="primary"
            loading={updateLoading}
            onClick={() =>
              handleUpdateSTRStatus(
                selectedSTR.report_id,
                "CLOSED",
                investigatorNotes
              )
            }
          >
            Close Case
          </Button>,
        ]}
      >
        {selectedSTR && (
          <Space
            direction="vertical"
            style={{ width: "100%" }}
            size="large"
          >
            <Descriptions bordered column={2}>
              <Descriptions.Item label="Report ID">
                {selectedSTR.report_id}
              </Descriptions.Item>

              <Descriptions.Item label="User">
                {selectedSTR.user_name}
              </Descriptions.Item>

              <Descriptions.Item label="Transaction">
                {selectedSTR.transaction_ref}
              </Descriptions.Item>

              <Descriptions.Item label="Amount">
                ${selectedSTR.amount}
              </Descriptions.Item>

              <Descriptions.Item label="Risk">
                {selectedSTR.risk_level}
              </Descriptions.Item>

              <Descriptions.Item label="Status">
                {selectedSTR.status}
              </Descriptions.Item>

              <Descriptions.Item
                label="Created"
                span={2}
              >
                {moment(
                  selectedSTR.created_at
                ).format("DD MMM YYYY HH:mm")}
              </Descriptions.Item>

              <Descriptions.Item
                label="Reason"
                span={2}
              >
                {selectedSTR.reason}
              </Descriptions.Item>
            </Descriptions>

            <div>
              <Text strong>
                Investigator Notes
              </Text>

              <TextArea
                rows={5}
                value={investigatorNotes}
                onChange={(e) =>
                  setInvestigatorNotes(
                    e.target.value
                  )
                }
                placeholder="Enter investigation notes..."
                style={{ marginTop: 10 }}
              />
            </div>
          </Space>
        )}
      </Modal>

      {/* RULE MODAL */}

      <Modal
        title={editingRule ? "Edit Monitoring Rule" : "Rule Creation Window"}
        open={ruleModalOpen}
        onCancel={() => {
          setRuleModalOpen(false);
          setEditingRule(null);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        confirmLoading={updateLoading}
        width={900}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveRule}
          initialValues={{
            is_active: true,
            risk_level: "MEDIUM",
            conditions: [{}],
          }}
        >
          {/* RULE DETAILS */}

          <Card
            size="small"
            title="Rule Details"
            style={{
              borderRadius: 12,
              marginBottom: 20,
            }}
          >
            <Form.Item
              name="name"
              label="Rule Name"
              rules={[
                {
                  required: true,
                  message:
                    "Please enter rule name",
                },
              ]}
            >
              <Input placeholder="e.g. High-value transaction review" />
            </Form.Item>

            <Form.Item
              name="description"
              label="Description"
            >
              <TextArea
                rows={4}
                placeholder="Describe what this rule does and when it should trigger..."
              />
            </Form.Item>
          </Card>

          {/* CONDITIONS */}

          <Card
            size="small"
            title="Conditions"
            style={{
              borderRadius: 12,
            }}

          >
            <Form.List name="conditions">
              {(fields, { add, remove }) => (
                <>
                  <div style={{ textAlign: "right", marginBottom: 20 }}>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() =>
                        add({
                          joiner: "AND",
                        })
                      }
                    >
                      Add Condition
                    </Button>
                  </div>

                  {fields.map((field, index) => (
                    <div key={field.key}>
                      <Row gutter={10} align="middle">
                        {/* FIELD */}
                        <Col span={8}>
                          <Form.Item
                            name={[field.name, "field"]}
                            rules={[
                              {
                                required: true,
                                message: "Select field",
                              },
                            ]}
                          >
                            <Select placeholder="Select field">
                              {conditionFields.map((item) => (
                                <Option
                                  key={item.value}
                                  value={item.value}
                                >
                                  {item.label}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>

                        {/* OPERATOR */}
                        <Col span={7}>
                          <Form.Item
                            name={[field.name, "operator"]}
                            rules={[
                              {
                                required: true,
                                message: "Select operator",
                              },
                            ]}
                          >
                            <Select placeholder="Operator">
                              {operators.map((item) => (
                                <Option
                                  key={item.value}
                                  value={item.value}
                                >
                                  {item.label}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>

                        {/* VALUE */}
                        <Col span={7}>
                          <Form.Item
                            name={[field.name, "value"]}
                            rules={[
                              {
                                required: true,
                                message: "Enter value",
                              },
                            ]}
                          >
                            <InputNumber
                              style={{ width: "100%" }}
                              placeholder="Value"
                            />
                          </Form.Item>
                        </Col>

                        {/* DELETE */}
                        <Col span={2}>
                          {fields.length > 1 && (
                            <Button
                              danger
                              type="text"
                              icon={<DeleteOutlined />}
                              onClick={() =>
                                remove(field.name)
                              }
                            />
                          )}
                        </Col>
                      </Row>

                      {/* JOINER */}
                      {index !== fields.length - 1 && (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            marginBottom: 20,
                          }}
                        >
                          <Form.Item
                            name={[field.name, "joiner"]}
                            initialValue="AND"
                            style={{ marginBottom: 0 }}
                          >
                            <Select style={{ width: 120 }}>
                              <Option value="AND">
                                AND
                              </Option>

                              <Option value="OR">
                                OR
                              </Option>
                            </Select>
                          </Form.Item>
                        </div>
                      )}

                      {index !== fields.length - 1 && (
                        <Divider />
                      )}
                    </div>
                  ))}
                </>
              )}
            </Form.List>

            {/* FOOTER OPTIONS */}

            <Row gutter={20}>
              <Col span={12}>
                <Form.Item
                  name="risk_level"
                  label="Risk Level"
                >
                  <Select>
                    <Option value="LOW">
                      Low
                    </Option>

                    <Option value="MEDIUM">
                      Medium
                    </Option>

                    <Option value="HIGH">
                      High
                    </Option>
                  </Select>
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  name="is_active"
                  label="Active"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
            </Row>
          </Card>
        </Form>
      </Modal>
    </div>
  );
}

export default ComplianceManager;